import React, { useState, useCallback, useMemo, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useIntl } from 'react-intl'
import { Box, TablePagination, Typography } from '@mui/material'
import { MaterialReactTable, useMaterialReactTable } from 'material-react-table'
import { MRT_Localization_ES } from 'material-react-table/locales/es'
import { tableStyles } from './table-global-styles'

/**
 * Función de utilidad para calcular el pinning de columnas basado en la configuración fixedColumns.
 * @private
 */
const calculateColumnPinning = (columns, fixedColumns) => {
  if (!fixedColumns || (!fixedColumns.left && !fixedColumns.right)) {
    return {}
  }

  const columnPinning = {}

  // Pin left columns
  if (fixedColumns.left && fixedColumns.left > 0) {
    const leftColumns = columns.slice(0, fixedColumns.left).map((col) => col.id || col.accessorKey)
    if (leftColumns.length > 0) {
      columnPinning.left = leftColumns
    }
  }

  // Pin right columns
  if (fixedColumns.right && fixedColumns.right > 0) {
    const rightColumns = columns.slice(-fixedColumns.right).map((col) => col.id || col.accessorKey)
    if (rightColumns.length > 0) {
      columnPinning.right = rightColumns
    }
  }

  return columnPinning
}

/**
 * Función de utilidad para combinar estilos globales con estilos personalizados.
 * @private
 */
const mergeStyles = (globalStyles, customStyles) => {
  if (!customStyles) return globalStyles
  return { ...globalStyles, ...customStyles }
}

/**
 * @typedef {import('material-react-table').MRT_ColumnDef} MRT_ColumnDef
 */

/**
 * @typedef {object} FixedColumnsConfig
 * @property {number} [left] - Número de columnas a fijar en el lado izquierdo.
 * @property {number} [right] - Número de columnas a fijar en el lado derecho.
 */

/**
 * @typedef {object} TableOptions
 * @property {FixedColumnsConfig} [fixedColumns] - Configuración de columnas fijas.
 * @property {boolean} [selection] - Si es `true`, habilita la selección de filas con checkboxes.
 */

/**
 * @typedef {object} SearchParams
 * @property {string} [_pagina] - El número de página actual.
 * @property {string} [_num_x_pagina] - El número de registros por página.
 */

/**
 * @typedef {object} TableUrlPaginationProps
 * @property {MRT_ColumnDef[]} columns - Las definiciones de columnas para la tabla.
 * @property {object[]} [data=[]] - Los datos a mostrar en la tabla.
 * @property {number} [totalEntries=0] - El número total de registros (para paginación).
 * @property {boolean} [isLoading=false] - Si es `true`, muestra el estado de carga.
 * @property {TableOptions} [options={}] - Opciones adicionales de configuración de la tabla.
 * @property {SearchParams} [search={}] - Parámetros de búsqueda externos (página, tamaño).
 * @property {function(React.MouseEvent, object): void} [onRowClick] - Callback que se ejecuta al hacer clic en una fila.
 * @property {function(object[]): void} [onSelectionChange] - Callback que se ejecuta cuando cambia la selección de filas.
 * @property {function | object} [muiTableBodyRowProps] - Props adicionales para las filas del cuerpo de la tabla.
 * @property {any} [resetSelectionTrigger] - Valor que cuando cambia, resetea la selección de filas.
 */

/**
 * Un componente de tabla avanzado con paginación, ordenamiento y sincronización con URL.
 *
 * Construido sobre Material React Table, proporciona una tabla completa con:
 * - Paginación manual sincronizada con parámetros de URL (`_pagina`, `_num_x_pagina`)
 * - Ordenamiento manual sincronizado con URL (`_order`, `_order_by`)
 * - Selección de filas opcional con checkboxes
 * - Columnas fijas (pinning) en los lados izquierdo y derecho
 * - Estilos globales consistentes con personalización por columna
 * - Localización en español
 * - Estados de carga y vacío
 *
 * **Nota:** Este componente requiere `react-router-dom` para la gestión de URL y `react-intl` para i18n.
 *
 * @component
 * @example
 * const MyDataTable = () => {
 *   const [data, setData] = useState([]);
 *   const [totalEntries, setTotalEntries] = useState(0);
 *   const [isLoading, setIsLoading] = useState(true);
 *   const location = useLocation();
 *   const searchParams = new URLSearchParams(location.search);
 *
 *   const columns = [
 *     { accessorKey: 'id', header: 'ID', size: 80 },
 *     { accessorKey: 'name', header: 'Nombre' },
 *     { accessorKey: 'email', header: 'Email' },
 *     { accessorKey: 'status', header: 'Estado' },
 *   ];
 *
 *   useEffect(() => {
 *     fetchData(searchParams).then((response) => {
 *       setData(response.data);
 *       setTotalEntries(response.total);
 *       setIsLoading(false);
 *     });
 *   }, [location.search]);
 *
 *   const handleRowClick = (event, row) => {
 *     console.log('Fila clickeada:', row);
 *   };
 *
 *   return (
 *     <TableUrlPagination
 *       columns={columns}
 *       data={data}
 *       totalEntries={totalEntries}
 *       isLoading={isLoading}
 *       onRowClick={handleRowClick}
 *       options={{
 *         fixedColumns: { left: 1, right: 1 },
 *         selection: true,
 *       }}
 *       search={{
 *         _pagina: searchParams.get('_pagina'),
 *         _num_x_pagina: searchParams.get('_num_x_pagina'),
 *       }}
 *     />
 *   );
 * };
 *
 * @param {TableUrlPaginationProps} props - Las props del componente TableUrlPagination.
 */
export const TableUrlPagination = ({
  columns,
  data = [],
  totalEntries = 0,
  isLoading = false,
  options = {},
  search = {},
  onRowClick,
  onSelectionChange,
  muiTableBodyRowProps,
  resetSelectionTrigger,
}) => {
  const intl = useIntl()
  const navigate = useNavigate()
  const location = useLocation()
  const query = useMemo(() => new URLSearchParams(location.search), [location.search])

  const { fixedColumns, ...restOptions } = options

  const urlPage = parseInt(query.get('_pagina'), 10) || 1
  const urlPerPage = parseInt(query.get('_num_x_pagina'), 10) || 10

  const page = parseInt(search['_pagina'], 10) || urlPage
  const pageSize = parseInt(search['_num_x_pagina'], 10) || urlPerPage

  const initialOrder = query.get('_order')
  const initialOrderBy = query.get('_order_by')
  const [sorting, setSorting] = useState(
    initialOrder && initialOrderBy ? [{ id: initialOrderBy, desc: initialOrder === 'desc' }] : []
  )

  const [pagination, setPagination] = useState({
    pageIndex: page - 1,
    pageSize: pageSize,
  })

  // Update pagination state when search params change from external sources
  useEffect(() => {
    const newPage = parseInt(search['_pagina'], 10) || 1
    const newPageSize = parseInt(search['_num_x_pagina'], 10) || 10

    setPagination((prevPagination) => {
      if (prevPagination.pageIndex !== newPage - 1 || prevPagination.pageSize !== newPageSize) {
        return {
          pageIndex: newPage - 1,
          pageSize: newPageSize,
        }
      }
      return prevPagination
    })
  }, [search['_pagina'], search['_num_x_pagina']])

  // Update pagination state and URL when pagination changes
  const handlePaginationChange = useCallback(
    (updaterOrValue) => {
      const newPagination =
        typeof updaterOrValue === 'function' ? updaterOrValue(pagination) : updaterOrValue
      setPagination(newPagination)

      query.set('_pagina', (newPagination.pageIndex + 1).toString())
      query.set('_num_x_pagina', newPagination.pageSize.toString())
      navigate({ search: '?' + query.toString() }, { replace: true })
    },
    [navigate, query, pagination]
  )

  // Update sorting state and URL when sorting changes
  const handleSortingChange = useCallback(
    (updaterOrValue) => {
      const newSorting =
        typeof updaterOrValue === 'function' ? updaterOrValue(sorting) : updaterOrValue
      setSorting(newSorting)

      query.set('_pagina', '1')
      if (newSorting.length > 0) {
        query.set('_order', newSorting[0].desc ? 'desc' : 'asc')
        query.set('_order_by', newSorting[0].id)
      } else {
        query.delete('_order')
        query.delete('_order_by')
      }
      navigate({ search: '?' + query.toString() }, { replace: true })
    },
    [navigate, query, sorting]
  )

  // Legacy handlers for TablePagination component
  const handlePageChange = useCallback(
    (_evt, newPage0) => {
      handlePaginationChange({ pageIndex: newPage0, pageSize: pagination.pageSize })
    },
    [handlePaginationChange, pagination.pageSize]
  )

  const handleRowsPerPageChange = useCallback(
    (evt) => {
      const newSize = parseInt(evt.target.value, 10)
      handlePaginationChange({ pageIndex: 0, pageSize: newSize })
    },
    [handlePaginationChange]
  )

  // Row selection state
  const [rowSelection, setRowSelection] = useState({})

  useEffect(() => {
    if (resetSelectionTrigger !== undefined) {
      setRowSelection({})
      if (onSelectionChange) {
        onSelectionChange([])
      }
    }
  }, [resetSelectionTrigger])

  const handleRowClick = useCallback(
    (row, event) => {
      if (onRowClick) {
        onRowClick(event, row.original)
      }
    },
    [onRowClick]
  )

  const handleRowSelectionChange = useCallback(
    (updaterOrValue) => {
      const newSelection =
        typeof updaterOrValue === 'function' ? updaterOrValue(rowSelection) : updaterOrValue
      setRowSelection(newSelection)
      if (onSelectionChange) {
        const selectedRowIds = Object.keys(newSelection).filter((key) => newSelection[key])
        const selectedRows = data.filter((row, index) => selectedRowIds.includes(index.toString()))
        onSelectionChange(selectedRows)
      }
    },
    [onSelectionChange, data, rowSelection]
  )

  // Process columns to apply global styles while preserving custom styles
  const processedColumns = useMemo(() => {
    return columns.map((column) => {
      const processedColumn = { ...column }

      if (processedColumn.muiTableHeadCellProps) {
        const existingHeaderSx = processedColumn.muiTableHeadCellProps.sx || {}
        processedColumn.muiTableHeadCellProps = {
          ...processedColumn.muiTableHeadCellProps,
          sx: mergeStyles(tableStyles.header, existingHeaderSx),
        }
      } else {
        processedColumn.muiTableHeadCellProps = {
          sx: tableStyles.header,
        }
      }

      if (processedColumn.muiTableBodyCellProps) {
        const existingCellSx = processedColumn.muiTableBodyCellProps.sx || {}
        processedColumn.muiTableBodyCellProps = {
          ...processedColumn.muiTableBodyCellProps,
          sx: mergeStyles(tableStyles.cell, existingCellSx),
        }
      } else {
        processedColumn.muiTableBodyCellProps = {
          sx: tableStyles.cell,
        }
      }

      return processedColumn
    })
  }, [columns])

  // Material React Table configuration
  const table = useMaterialReactTable({
    columns: processedColumns,
    data: data,
    manualPagination: true,
    manualSorting: true,
    rowCount: totalEntries,
    enableColumnPinning: true,
    localization: MRT_Localization_ES,
    state: {
      pagination,
      sorting,
      rowSelection,
      isLoading,
    },
    initialState: {
      columnPinning: calculateColumnPinning(columns, fixedColumns),
    },
    muiTablePaperProps: {
      elevation: 0,
      sx: {
        '& .MuiTableHead-root .MuiTableCell-head': {
          backgroundColor: 'rgba(0, 65, 101, 0.1)',
          boxSizing: 'border-box',
          height: '56px',
          verticalAlign: 'middle',
          padding: '8px 16px',
          alignItems: 'center',
        },
        '& .MuiTableHead-root .MuiTableCell-head[data-pinned="true"]': {
          backgroundColor: 'white',
          boxSizing: 'border-box',
          height: '56px',
          verticalAlign: 'middle',
          padding: '8px 16px',
        },
        '& .MuiTableCell-head[data-pinned="true"]:before': {
          backgroundColor: 'rgba(0, 65, 101, 0.1)',
        },
        '& .MuiTableCell-root': {
          boxSizing: 'border-box',
        },
        '& .MuiTableBody-root .MuiTableCell-root': {
          boxSizing: 'border-box',
        },
        '& .MuiTableCell-root[data-select="true"]': {
          padding: '8px 16px',
          height: '40px',
          display: 'table-cell',
          verticalAlign: 'middle',
          textAlign: 'center',
        },
        '& .mrt-row-select .MuiCheckbox-root': {
          padding: '4px',
          margin: 0,
        },
        // Ensure header cells with multiple lines align properly
        '& .MuiTableHead-root .MuiTableCell-head .mrt-table-head-cell-content': {
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '40px',
          width: '100%',
        },
        // Fix for tooltip icons in headers
        '& .MuiTableHead-root .MuiTableCell-head .MuiSvgIcon-root': {
          verticalAlign: 'middle',
        },
        // Ensure consistent header height for text and icon headers
        '& .MuiTableHead-root .MuiTableCell-head': {
          '&:has(.MuiSvgIcon-root)': {
            textAlign: 'center',
          },
        },
        // Hide sort arrows for icon columns while keeping functionality
        '& .MuiTableHead-root .MuiTableCell-head:has(.MuiSvgIcon-root) .MuiTableSortLabel-root .MuiTableSortLabel-icon':
          {
            display: 'none !important',
          },
      },
    },
    muiTableHeadProps: {
      sx: {
        '& .MuiTableCell-head': {
          backgroundColor: 'rgba(0, 65, 101, 0.1)',
          fontSize: 14,
          color: '#1a171b',
          border: 'none',
          boxSizing: 'border-box',
          height: '56px',
          verticalAlign: 'middle',
          display: 'table-cell',
          padding: '8px 16px',
        },
        '& .MuiTableCell-head[data-select="true"]': {
          padding: '8px 16px',
          height: '56px',
          display: 'table-cell',
          verticalAlign: 'middle',
          textAlign: 'center',
        },
      },
    },
    muiSelectCheckboxProps: {
      sx: {
        '& .MuiTableCell-head': {
          backgroundColor: 'rgba(0, 65, 101, 0.1)',
          boxSizing: 'border-box',
          padding: '8px 16px',
          height: '56px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          verticalAlign: 'middle',
        },
        '& .MuiCheckbox-root': {
          padding: '4px',
          margin: 0,
        },
      },
    },
    muiSelectAllCheckboxProps: {
      sx: {
        padding: '4px',
        margin: 0,
      },
    },
    onPaginationChange: handlePaginationChange,
    onSortingChange: handleSortingChange,
    onRowSelectionChange: handleRowSelectionChange,
    enableRowSelection: options.selection || false,
    enableSorting: true,
    enablePagination: false,
    enableBottomToolbar: false,
    enableTopToolbar: false,
    enableColumnActions: false,
    enableColumnFilters: false,
    enableGlobalFilter: false,
    enableFullScreenToggle: false,
    enableDensityToggle: false,

    muiTableBodyRowProps: ({ row }) => {
      const externalProps =
        typeof muiTableBodyRowProps === 'function' ? muiTableBodyRowProps({ row }) : {}

      const internalProps = {
        onClick: onRowClick ? (event) => handleRowClick(row, event) : undefined,
        sx: {
          cursor: onRowClick ? 'pointer' : 'default',
          '&:hover': onRowClick
            ? {
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
              }
            : {},
        },
      }

      return {
        ...externalProps,
        ...internalProps,
        sx: {
          ...externalProps.sx,
          ...internalProps.sx,
        },
      }
    },

    renderEmptyRowsFallback: () => (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '200px',
          width: '100%',
          padding: 2,
        }}
      >
        <Typography
          variant="body1"
          sx={{
            color: '#63666a',
            fontSize: '14px',
            textAlign: 'center',
          }}
        >
          {intl.formatMessage({ id: 'table.noResults' })}
        </Typography>
      </Box>
    ),
    ...restOptions,
  })

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
      <Box sx={{ flexGrow: 1, position: 'relative', height: '100%' }}>
        <MaterialReactTable table={table} />
      </Box>

      <TablePagination
        sx={{ backgroundColor: '#fff', border: '1px solid #e0e0e0' }}
        component="div"
        count={totalEntries}
        page={pagination.pageIndex}
        rowsPerPage={pagination.pageSize}
        onPageChange={handlePageChange}
        onRowsPerPageChange={handleRowsPerPageChange}
        rowsPerPageOptions={[
          { value: 5, label: '5' },
          { value: 10, label: '10' },
          { value: 20, label: '20' },
        ]}
        labelRowsPerPage="Registros"
        showFirstButton
        showLastButton
      />
    </Box>
  )
}
